import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import noop from 'lib/function/noop'
import { createHomepageOfferClaimStore, type HomepageOfferClaimStore } from './homepageOfferClaimStore'

const EMPTY_HERO_OFFER_IDS: Array<string> = []

const HomepageOfferDedupContext = createContext<HomepageOfferClaimStore | undefined>(undefined)

interface HeroOfferPublication {
  offerIds: Array<string>
  ready: boolean
  key: string
}

const EMPTY_HERO_PUBLICATION: HeroOfferPublication = {
  offerIds: EMPTY_HERO_OFFER_IDS,
  ready: false,
  key: '',
}

interface HomepageHeroOfferIdsContextValue {
  publication: HeroOfferPublication
  getCurrentPublication: () => HeroOfferPublication
  publishOfferList: (offerList: App.OfferList) => void
  clearOfferIds: () => void
}

const HomepageHeroOfferIdsContext = createContext<HomepageHeroOfferIdsContextValue>({
  publication: EMPTY_HERO_PUBLICATION,
  getCurrentPublication: () => EMPTY_HERO_PUBLICATION,
  publishOfferList: noop,
  clearOfferIds: noop,
})

interface Props {
  children: ReactNode
}

function HomepageOfferDedupProvider(props: Props) {
  const { children } = props
  const store = useMemo(() => createHomepageOfferClaimStore(), [])
  const [publication, setPublication] = useState(EMPTY_HERO_PUBLICATION)
  const currentPublication = useRef(EMPTY_HERO_PUBLICATION)
  const getCurrentPublication = useCallback(() => currentPublication.current, [])
  const publishOfferList = useCallback((offerList: App.OfferList) => {
    const current = currentPublication.current
    const { offerIds, key, fetching, streamEnded } = offerList
    const ready = !fetching && streamEnded !== false
    if (current.key === key && current.ready === ready
      && current.offerIds.length === offerIds.length
      && current.offerIds.every((offerId, index) => offerId === offerIds[index])) return
    const next = { offerIds, key, ready }
    currentPublication.current = next
    setPublication(next)
  }, [])
  const clearOfferIds = useCallback(() => {
    currentPublication.current = EMPTY_HERO_PUBLICATION
    setPublication(EMPTY_HERO_PUBLICATION)
  }, [])
  const heroOfferIdsValue = useMemo(() => ({
    publication,
    getCurrentPublication,
    publishOfferList,
    clearOfferIds,
  }), [clearOfferIds, getCurrentPublication, publication, publishOfferList])

  return (
    <HomepageOfferDedupContext.Provider value={store}>
      <HomepageHeroOfferIdsContext.Provider value={heroOfferIdsValue}>
        {children}
      </HomepageHeroOfferIdsContext.Provider>
    </HomepageOfferDedupContext.Provider>
  )
}

export function useHomepageOfferClaimStore(): HomepageOfferClaimStore | undefined {
  return useContext(HomepageOfferDedupContext)
}

export function useHomepageHeroOfferIds(): Array<string> {
  return useContext(HomepageHeroOfferIdsContext).publication.offerIds
}

export function useHomepageHeroOfferIdsPublished(): boolean {
  return useContext(HomepageHeroOfferIdsContext).publication.ready
}

export function useHomepageHeroOfferPublication() {
  return useContext(HomepageHeroOfferIdsContext)
}

export function HomepageHeroOfferIdsReporter(props: { offerList: App.OfferList }) {
  const { offerList } = props
  const { publishOfferList, clearOfferIds } = useContext(HomepageHeroOfferIdsContext)

  useEffect(() => {
    publishOfferList(offerList)
  }, [offerList, publishOfferList])

  useEffect(() => {
    return () => clearOfferIds()
  }, [clearOfferIds])

  return null
}

export default HomepageOfferDedupProvider
